import type {
	AdminSettingsResponse,
	AdminStats,
	AdminSubscription,
	AdminUserDetail,
	AdminUserRow,
	ApiError,
	BillingConfigResponse,
	CreateCheckoutSessionRequest,
	CreateCheckoutSessionResponse,
	DayCalculationRequest,
	DayCalculationResponse,
	GetPurchasesResponse,
	HealthResponse,
	OpenApiSpec,
	TerrainSunRequest,
	TerrainSunResponse,
	UsageResponse,
	VerifyPurchaseRequest,
	VerifyPurchaseResponse,
	VersionResponse,
	YearAccessResponse,
	YearCalculationRequest,
	YearCalculationResponse,
} from "../types/api";

const API_BASE = "/api/v1";
const CONSOLE_BASE = "/api/console";
const BILLING_BASE = "/api/billing";
const ACCOUNT_BASE = "/api/account";

class ApiError401 extends Error {
	status = 401;
	constructor(message = "Unauthorized") {
		super(message);
		this.name = "ApiError401";
	}
}

/** An API error that carries the server's machine-readable `code`. */
class ApiCodedError extends Error {
	code?: string;
	constructor(message: string, code?: string) {
		super(message);
		this.name = "ApiCodedError";
		this.code = code;
	}
}

export { ApiCodedError, ApiError401 };

export interface RateLimit {
	limit: number;
	remaining: number;
}

class ApiClient {
	private lastRateLimit: RateLimit | null = null;

	/** The most recent quota headers seen on a metered (console) response, if any. */
	getRateLimit(): RateLimit | null {
		return this.lastRateLimit;
	}

	private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
		const response = await fetch(endpoint, {
			headers: {
				"Content-Type": "application/json",
				...options.headers,
			},
			credentials: "same-origin",
			...options,
		});

		const limit = response.headers.get("x-ratelimit-limit");
		const remaining = response.headers.get("x-ratelimit-remaining");
		if (limit !== null && remaining !== null) {
			this.lastRateLimit = { limit: Number(limit), remaining: Number(remaining) };
		}

		if (response.status === 401) {
			throw new ApiError401();
		}

		if (response.status === 429) {
			const body: ApiError = await response.json().catch(() => ({ error: "quota_exceeded" }));
			if (body.error === "quota_exceeded") {
				throw new Error("Kvoten er brukt opp for denne perioden.");
			}
			// e.g. "rate_limited": the per-visitor cap on free day calculations.
			throw new ApiCodedError(body.message || body.error || "For mange forespørsler", body.error);
		}

		if (!response.ok) {
			const error: ApiError = await response.json().catch(() => ({
				error: `HTTP ${response.status}`,
				message: response.statusText,
			}));
			// `error` doubles as a machine-readable code (e.g. "year_access_required").
			throw new ApiCodedError(error.message || error.error, error.error);
		}

		return response.json();
	}

	async health(): Promise<HealthResponse> {
		return this.request<HealthResponse>("/healthz");
	}

	async version(): Promise<VersionResponse> {
		return this.request<VersionResponse>("/version");
	}

	async calculateDay(request: DayCalculationRequest): Promise<DayCalculationResponse> {
		return this.request<DayCalculationResponse>(`${API_BASE}/day`, {
			method: "POST",
			body: JSON.stringify(request),
		});
	}

	async calculateYear(request: YearCalculationRequest): Promise<YearCalculationResponse> {
		return this.request<YearCalculationResponse>(`${API_BASE}/year`, {
			method: "POST",
			body: JSON.stringify(request),
		});
	}

	// Console calc endpoints: session-authenticated and metered against the
	// subscription quota (no purchase). Same request/response shapes as the
	// public calc; only the path differs.
	async consoleCalculateDay(request: DayCalculationRequest): Promise<DayCalculationResponse> {
		return this.request<DayCalculationResponse>(`${CONSOLE_BASE}/day`, {
			method: "POST",
			body: JSON.stringify(request),
		});
	}

	async consoleCalculateYear(request: YearCalculationRequest): Promise<YearCalculationResponse> {
		return this.request<YearCalculationResponse>(`${CONSOLE_BASE}/year`, {
			method: "POST",
			body: JSON.stringify(request),
		});
	}

	// Billing methods (account-based)

	async getStripeConfig(): Promise<BillingConfigResponse> {
		return this.request<BillingConfigResponse>(`${BILLING_BASE}/config`);
	}

	async createCheckoutSession(request: CreateCheckoutSessionRequest): Promise<CreateCheckoutSessionResponse> {
		// Works for guests — Stripe collects the email and the account is created
		// (or linked) when the payment completes.
		return this.request<CreateCheckoutSessionResponse>(`${BILLING_BASE}/checkout`, {
			method: "POST",
			body: JSON.stringify(request),
		});
	}

	async verifyPurchase(request: VerifyPurchaseRequest): Promise<VerifyPurchaseResponse> {
		// Public: a valid paid sessionId is proof of purchase. Records idempotently
		// and returns the purchase details + buyer email for the success page.
		return this.request<VerifyPurchaseResponse>(`${BILLING_BASE}/verify`, {
			method: "POST",
			body: JSON.stringify(request),
		});
	}

	async getPurchases(): Promise<GetPurchasesResponse> {
		// Returns purchases for the currently logged-in user; throws ApiError401 if not logged in.
		return this.request<GetPurchasesResponse>(`${BILLING_BASE}/purchases`);
	}

	async getAccess(params: { latitude: number; longitude: number }): Promise<YearAccessResponse> {
		const { latitude, longitude } = params;
		const qs = new URLSearchParams({
			latitude: String(latitude),
			longitude: String(longitude),
		});
		return this.request<YearAccessResponse>(`${BILLING_BASE}/access?${qs}`);
	}

	async getTerrainSun(request: TerrainSunRequest): Promise<TerrainSunResponse> {
		return this.request<TerrainSunResponse>(`${API_BASE}/terrain-sun`, {
			method: "POST",
			body: JSON.stringify(request),
		});
	}

	async getUsage(): Promise<UsageResponse> {
		return this.request<UsageResponse>(`${BILLING_BASE}/usage`);
	}

	/** Whether the account has a password, i.e. change vs. set. */
	async getPasswordStatus(): Promise<{ hasPassword: boolean }> {
		return this.request<{ hasPassword: boolean }>(`${ACCOUNT_BASE}/password-status`);
	}

	/** Sets a first password for accounts created via magic link or guest checkout. */
	async setPassword(newPassword: string): Promise<{ ok: true }> {
		return this.request<{ ok: true }>(`${ACCOUNT_BASE}/set-password`, {
			method: "POST",
			body: JSON.stringify({ newPassword }),
		});
	}

	/**
	 * Permanently deletes the account. `confirm` must be the account's email.
	 * Uses its own fetch so the server's error `code` survives — the caller needs
	 * it to tell "cancel your subscription first" from a generic failure.
	 */
	async deleteAccount(confirm: string): Promise<{ ok: true }> {
		const response = await fetch(`${ACCOUNT_BASE}/delete`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "same-origin",
			body: JSON.stringify({ confirm }),
		});

		if (!response.ok) {
			const body: { error?: string; code?: string } = await response.json().catch(() => ({}));
			throw new ApiCodedError(body.error ?? `HTTP ${response.status}`, body.code);
		}

		return response.json();
	}

	async getAdminSettings(): Promise<AdminSettingsResponse> {
		return this.request<AdminSettingsResponse>(`/api/admin/settings`);
	}

	async adminUpdateSetting(key: string, value: number | string): Promise<AdminSettingsResponse> {
		return this.request<AdminSettingsResponse>(`/api/admin/settings/${key}`, {
			method: "PUT",
			body: JSON.stringify({ value }),
		});
	}

	async adminResetSetting(key: string): Promise<AdminSettingsResponse> {
		return this.request<AdminSettingsResponse>(`/api/admin/settings/${key}`, {
			method: "DELETE",
		});
	}

	async getAdminStats(): Promise<AdminStats> {
		return this.request<AdminStats>(`/api/admin/stats`);
	}

	async getAdminUsers(query = "", limit = 25, offset = 0): Promise<{ users: AdminUserRow[] }> {
		const qs = new URLSearchParams({ query, limit: String(limit), offset: String(offset) });
		return this.request<{ users: AdminUserRow[] }>(`/api/admin/users?${qs.toString()}`);
	}

	async getAdminUser(id: string): Promise<AdminUserDetail> {
		return this.request<AdminUserDetail>(`/api/admin/users/${id}`);
	}

	async adminGrantAccess(
		id: string,
		body: { latitude: number; longitude: number; address?: string },
	): Promise<{ id: string }> {
		return this.request<{ id: string }>(`/api/admin/users/${id}/grant-access`, {
			method: "POST",
			body: JSON.stringify(body),
		});
	}

	async adminRevokeAccess(purchaseId: string): Promise<{ ok: boolean }> {
		return this.request<{ ok: boolean }>(`/api/admin/purchases/${purchaseId}`, {
			method: "DELETE",
		});
	}

	async adminBanUser(id: string, reason?: string): Promise<{ ok: boolean }> {
		return this.request<{ ok: boolean }>(`/api/admin/users/${id}/ban`, {
			method: "POST",
			body: JSON.stringify({ reason }),
		});
	}

	async adminUnbanUser(id: string): Promise<{ ok: boolean }> {
		return this.request<{ ok: boolean }>(`/api/admin/users/${id}/unban`, { method: "POST" });
	}

	async adminDeleteUser(id: string): Promise<{ ok: boolean }> {
		return this.request<{ ok: boolean }>(`/api/admin/users/${id}`, { method: "DELETE" });
	}

	async adminGrantPro(id: string): Promise<{ ok: boolean }> {
		return this.request<{ ok: boolean }>(`/api/admin/users/${id}/grant-pro`, { method: "POST" });
	}

	async adminRemovePro(id: string): Promise<{ ok: boolean }> {
		return this.request<{ ok: boolean }>(`/api/admin/users/${id}/remove-pro`, { method: "POST" });
	}

	async getAdminSubscriptions(status?: string): Promise<{ subscriptions: AdminSubscription[] }> {
		const qs = status ? `?status=${encodeURIComponent(status)}` : "";
		return this.request<{ subscriptions: AdminSubscription[] }>(`/api/admin/subscriptions${qs}`);
	}

	async adminCancelSubscription(id: string): Promise<{ ok: boolean }> {
		return this.request<{ ok: boolean }>(`/api/admin/subscriptions/${id}/cancel`, {
			method: "POST",
		});
	}

	async getOpenApiSpec(): Promise<OpenApiSpec> {
		return this.request<OpenApiSpec>(`/api/openapi.json`);
	}
}

export const api = new ApiClient();
